export {default as data} from './index.json';
import {Component} from 'intact';
import template from './index.vdt';
import './index.styl'; 

import {createVNode} from 'intact';
import {bind, Table, TableColumn} from 'kpc';
import type {
    XMarkdownTagAttrs,
    XMarkdownTagAttrsContext,
    XMarkdownTagRenderContext,
    XMarkdownTagRenderers,
} from 'kpc';

interface Props {
    content: string
    typing: boolean | {
        interval: number
        step: number
    }
    sanitizeOptions: Record<string, any>
}

interface TableColumnItem {
    key: string
    title: string
}

type TableRowItem = {
    id: string
    [key: string]: string
};

const CONTENT = `# 标签增强示例

<notice-card title="发布建议">优先把核心交互、Demo 和单测一起补齐，再进入发版检查。</notice-card>

| 阶段 | 负责人 | 状态 |
| --- | --- | --- |
| 设计评审 | Daisy | 已完成 |
| 联调验证 | Alex | 进行中 |

更多规范可以参考 [King Design](https://design.ksyun.com/)。

> 这段普通引用块仍然保持默认渲染。`;

const TYPING = {
    interval: 18,
    step: 3,
};

export default class extends Component<Props> {
    static template = template;

    private restartTimer: number | null = null;

    static defaults() {
        return {
            content: CONTENT,
            typing: false,
            sanitizeOptions: {
                ADD_TAGS: ['notice-card'],
                ADD_ATTR: ['title'],
            },
        };
    }

    getTagAttrs(): XMarkdownTagAttrs {
        return {
            a: (context: XMarkdownTagAttrsContext) => /^(https?:)?\/\//i.test(context.attrs.href || '')
                ? {
                    className: 'xmarkdown-demo-tag-link',
                    'data-track': 'xmarkdown-demo-link',
                }
                : undefined,
        };
    }

    getTagRenderers(): XMarkdownTagRenderers {
        return {
            'notice-card': (context: XMarkdownTagRenderContext) => createVNode(
                'div',
                {className: 'xmarkdown-demo-tag-card'},
                [
                    createVNode(
                        'div',
                        {className: 'xmarkdown-demo-tag-card-title'},
                        context.attrs.title || '发布建议'
                    ),
                    createVNode(
                        'div',
                        {className: 'xmarkdown-demo-tag-card-body'},
                        context.textContent
                    ),
                ]
            ),
            table: (context: XMarkdownTagRenderContext) => {
                const parsed = parseMarkdownTable(context.html);
                if (!parsed) return undefined;

                return createVNode(
                    'div',
                    {className: 'xmarkdown-demo-tag-table'},
                    [
                        createVNode(
                            Table as any,
                            {
                                data: parsed.data,
                                rowKey: (item: TableRowItem) => item.id,
                                checkType: 'none',
                            },
                            parsed.columns.map((column) => createVNode(TableColumn as any, {
                                key: column.key,
                                title: column.title,
                            }))
                        ),
                    ]
                );
            },
        };
    }

    @bind
    restartTyping() {
        if (this.restartTimer) {
            clearTimeout(this.restartTimer);
        }

        this.set('typing', false);
        this.set('content', '');
        this.restartTimer = window.setTimeout(() => {
            this.restartTimer = null;
            this.set('typing', TYPING);
            this.set('content', CONTENT);
        }, 0);
    }
}

function parseMarkdownTable(html: string) {
    if (typeof DOMParser === 'undefined') return null;

    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');
    const table = document.querySelector('table');
    if (!table) return null;

    const headerCells = Array.from(table.querySelectorAll('thead th'));
    if (!headerCells.length) return null;

    const columns: TableColumnItem[] = headerCells.map((cell, index) => ({
        key: `column-${index}`,
        title: cell.textContent?.trim() || `列 ${index + 1}`,
    }));

    const data: TableRowItem[] = Array.from(table.querySelectorAll('tbody tr')).map((row, rowIndex) => {
        return columns.reduce<TableRowItem>((item, column, columnIndex) => {
            const cell = row.children[columnIndex] as HTMLElement | undefined;
            item[column.key] = cell?.textContent?.trim() || '';
            return item;
        }, {
            id: `row-${rowIndex}`,
        });
    });

    return {columns, data};
}